import { useState, useMemo } from 'react';
import i18next from 'i18next';

const fmt = new Intl.NumberFormat('en-US');

const COLUMNS = [
  { key: 'name', labelKey: '州属', numeric: false },
  { key: 'seats', labelKey: '议席', numeric: true },
  { key: 'registered', labelKey: '选民', numeric: true },
  { key: 'cast', labelKey: '投票数', numeric: true },
  { key: 'turnout', labelKey: '投票率', numeric: true },
];

const StateVotingTable = ({ states }) => {
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const rows = useMemo(() => {
    return states
      .filter((s) => s.voting)
      .map((s) => {
        const v = s.voting;
        const cast = v.ballotsIssued - v.ballotsNotReturned;
        return {
          name: s.name,
          seats: s.seats,
          registered: v.registered,
          cast,
          turnout: v.turnout,
        };
      });
  }, [states]);

  const sorted = useMemo(() => {
    if (sortKey === 'name') {
      const order = states.map((s) => s.name);
      return [...rows].sort((a, b) => {
        const cmp = order.indexOf(a.name) - order.indexOf(b.name);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return [...rows].sort((a, b) => {
      const av = a[sortKey] ?? -Infinity;
      const bv = b[sortKey] ?? -Infinity;
      const cmp = av - bv;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir, states]);

  if (rows.length === 0) {
    return (
      <p className="state-voting-table__empty">{i18next.t('投票数据暂缺')}</p>
    );
  }

  const onSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  return (
    <div className="state-voting-table">
      <table>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={col.numeric ? 'is-numeric' : ''}
                onClick={() => onSort(col.key)}
                aria-sort={
                  sortKey === col.key
                    ? sortDir === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
              >
                {i18next.t(col.labelKey)}
                {sortKey === col.key && (
                  <span className="state-voting-table__caret" aria-hidden="true">
                    {sortDir === 'asc' ? '▲' : '▼'}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.name}>
              <td>{r.name}</td>
              <td className="is-numeric">{r.seats}</td>
              <td className="is-numeric">{fmt.format(r.registered)}</td>
              <td className="is-numeric">{fmt.format(r.cast)}</td>
              <td className="is-numeric">
                {r.turnout === null ? '—' : `${r.turnout.toFixed(2)}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StateVotingTable;
